import { createMultiStyleConfigHelpers, defineStyle } from '@chakra-ui/react';
import { avatarAnatomy } from '@chakra-ui/anatomy';
import getThemes, { ThemesKeys } from './themeShared';

const style = createMultiStyleConfigHelpers(avatarAnatomy.keys);

const baseStyle = (theme: ThemesKeys) =>
  style.definePartsStyle({
    container: {
      bg: getThemes(theme).colors.palette.primary[50],
      fontWeight: '600',
      color: getThemes(theme).colors.palette.primary[100],
      overflow: 'hidden',
    },
  });

const profile = defineStyle({
  width: '28px',
  height: '28px',
  fontSize: 'sm',
  size: '14px',
});

const selectSm = (theme: ThemesKeys) =>
  defineStyle({
    '--avatar-size': getThemes(theme).sizes[5],
    '--avatar-font-size': getThemes(theme).fontSizes.xs,
  });

const selectMd = (theme: ThemesKeys) =>
  defineStyle({
    '--avatar-size': getThemes(theme).sizes[6],
    '--avatar-font-size': getThemes(theme).fontSizes.sm,
  });

const selectLg = (theme: ThemesKeys) =>
  defineStyle({
    '--avatar-size': getThemes(theme).sizes[8],
    '--avatar-font-size': getThemes(theme).fontSizes.lg,
  });

const buttonSm = (theme: ThemesKeys) =>
  defineStyle({
    '--avatar-size': getThemes(theme).sizes[6],
    '--avatar-font-size': getThemes(theme).fontSizes.xs,
  });

const buttonMd = (theme: ThemesKeys) =>
  defineStyle({
    '--avatar-size': getThemes(theme).sizes[7],
    '--avatar-font-size': getThemes(theme).fontSizes.sm,
  });

const buttonLg = (theme: ThemesKeys) =>
  defineStyle({
    '--avatar-size': getThemes(theme).sizes[10],
    '--avatar-font-size': getThemes(theme).fontSizes.lg,
  });

const xs3 = (theme: ThemesKeys) =>
  defineStyle({
    '--avatar-size': getThemes(theme).sizes[5],
    '--avatar-font-size': getThemes(theme).fontSizes.xs,
  });

const xs2 = (theme: ThemesKeys) =>
  defineStyle({
    '--avatar-size': getThemes(theme).sizes[6],
    '--avatar-font-size': getThemes(theme).fontSizes.sm,
  });

const xs = (theme: ThemesKeys) =>
  defineStyle({
    '--avatar-size': getThemes(theme).sizes[8],
    '--avatar-font-size': getThemes(theme).fontSizes.sm,
  });

const sm = (theme: ThemesKeys) =>
  defineStyle({
    '--avatar-size': getThemes(theme).sizes[10],
    '--avatar-font-size': getThemes(theme).fontSizes.lg,
  });

const md = (theme: ThemesKeys) =>
  defineStyle({
    '--avatar-size': getThemes(theme).sizes[12],
    '--avatar-font-size': getThemes(theme).fontSizes.xl,
  });

const lg = (theme: ThemesKeys) =>
  defineStyle({
    '--avatar-size': getThemes(theme).sizes[14],
    '--avatar-font-size': getThemes(theme).fontSizes['2xl'],
  });

const xl = (theme: ThemesKeys) =>
  defineStyle({
    '--avatar-size': getThemes(theme).sizes[16],
    '--avatar-font-size': getThemes(theme).fontSizes['3xl'],
  });

const xl2 = (theme: ThemesKeys) =>
  defineStyle({
    '--avatar-size': getThemes(theme).sizes[20],
    '--avatar-font-size': getThemes(theme).fontSizes['4xl'],
  });

const xl3 = (theme: ThemesKeys) =>
  defineStyle({
    '--avatar-size': getThemes(theme).sizes[24],
    '--avatar-font-size': getThemes(theme).fontSizes['5xl'],
  });

const xl4 = (theme: ThemesKeys) =>
  defineStyle({
    '--avatar-size': getThemes(theme).sizes[28],
    '--avatar-font-size': getThemes(theme).fontSizes['6xl'],
    size: getThemes(theme).space[28],
  });

const row = () =>
  style.definePartsStyle({
    container: {
      borderRadius: 7,
      img: {
        borderRadius: 0,
      },
    },
  });

const square = () =>
  style.definePartsStyle({
    container: {
      borderRadius: 7,
      img: {
        borderRadius: 0,
      },
    },
  });

const sizes = (theme: ThemesKeys) => ({
  profile: style.definePartsStyle({ container: profile }),
  selectSm: style.definePartsStyle({ container: selectSm(theme) }),
  selectMd: style.definePartsStyle({ container: selectMd(theme) }),
  selectLg: style.definePartsStyle({ container: selectLg(theme) }),
  buttonSm: style.definePartsStyle({ container: buttonSm(theme) }),
  buttonMd: style.definePartsStyle({ container: buttonMd(theme) }),
  buttonLg: style.definePartsStyle({ container: buttonLg(theme) }),
  '3xs': style.definePartsStyle({ container: xs3(theme) }), // 5
  '2xs': style.definePartsStyle({ container: xs2(theme) }), // 6
  xs: style.definePartsStyle({ container: xs(theme) }), // 8
  sm: style.definePartsStyle({ container: sm(theme) }), // 10
  md: style.definePartsStyle({ container: md(theme) }), // 12
  lg: style.definePartsStyle({ container: lg(theme) }), // 14
  xl: style.definePartsStyle({ container: xl(theme) }), // 16
  '2xl': style.definePartsStyle({ container: xl2(theme) }), // 20
  '3xl': style.definePartsStyle({ container: xl3(theme) }), // 24
  '4xl': style.definePartsStyle({ container: xl4(theme) }), // 28
});

export const avatarTheme = (theme: ThemesKeys) =>
  style.defineMultiStyleConfig({
    baseStyle: baseStyle(theme),
    variants: {
      row: row(),
      square: square(),
    },
    sizes: sizes(theme),
  });

export default (theme: ThemesKeys) => avatarTheme(theme);
